import { css } from 'aphrodite';
import DOMPurify from 'dompurify';
import * as React from 'react';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import type { ISupportThread } from '../../../models/Support/ISupportThread';
import type { ISupportTicket } from '../../../models/Support/ISupportTicket';
import { ThreadType } from '../../../models/Support/SupportEnums';
import { useSupportThreadsQuery } from '../../../queries/Support/useSupportThreadsQuery';
import {
	getInitials,
	getRepComposer,
	getSupportContactProfilePicSrc,
	getSupportUserProfilePicSrc,
} from '../../../utils/Support/supportHelpers';
import { Avatar } from '@HoudiniUI/Avatar';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { Popover, PopoverAnchor, PopoverArrow, PopoverContent } from '../../../web/components-v2/Popover';
import { ticketHoverPopoverStyles as hp } from './styles/ticketHoverPopoverStyles';
import { formatShortDateTime } from './supportTicketPresentation';

const OPEN_DELAY_MS = 500;
// Allow the pointer to cross the gap into the popover before closing it.
const CLOSE_DELAY_MS = 150;

const PREVIEW_THREAD_TYPES = [
	ThreadType.CustomerMessage,
	ThreadType.AgentReply,
	ThreadType.Forward,
	ThreadType.Note,
	ThreadType.AutoAcknowledgement,
];

const getThreadPreviewText = (thread: ISupportThread): string => {
	const text =
		thread.bodyPlainText ||
		new DOMParser().parseFromString(DOMPurify.sanitize(thread.body || ''), 'text/html').body.textContent ||
		'';
	return text.replace(/\s+/g, ' ').trim();
};

interface PreviewMessageProps {
	label: string;
	thread: ISupportThread;
	ticket: ISupportTicket;
	agentNames: Record<string, string>;
	agentProfilePicMap: Record<string, string>;
	agentProfilePicEmailMap: Record<string, string>;
}

const PreviewMessage: React.FC<PreviewMessageProps> = ({
	label,
	thread,
	ticket,
	agentNames,
	agentProfilePicMap,
	agentProfilePicEmailMap,
}) => {
	const userSession = useAdminUserSession();
	const currentUser = userSession?.user;
	const isAgentAuthored = thread.type !== ThreadType.CustomerMessage;
	const repComposer = getRepComposer(thread, ticket);
	const authorName =
		repComposer?.name ||
		thread.authorName ||
		(isAgentAuthored && thread.authorId ? agentNames[thread.authorId] : undefined) ||
		thread.authorEmail ||
		(isAgentAuthored ? undefined : ticket.contactName || ticket.contactEmail) ||
		'Unknown sender';
	const currentUserEmail = currentUser?.email?.trim().toLowerCase();
	const isYou = repComposer
		? repComposer.id === currentUser?.id
		: (!!thread.authorId && thread.authorId === currentUser?.id) ||
			(!!thread.authorEmail && !!currentUserEmail && thread.authorEmail.trim().toLowerCase() === currentUserEmail);
	const authorEmailProfilePic = thread.authorEmail
		? agentProfilePicEmailMap[thread.authorEmail.trim().toLowerCase()]
		: undefined;
	const authorUserProfilePicSrc = getSupportUserProfilePicSrc({
		profilePic: isYou ? currentUser?.profilePic : undefined,
		authorProfilePic: thread.authorProfilePic,
		agentProfilePic: (thread.authorId ? agentProfilePicMap[thread.authorId] : undefined) || authorEmailProfilePic,
		userId: thread.authorId || (isYou ? currentUser?.id : undefined),
		accountId: userSession.account?.id,
	});
	const avatarImgSrc = repComposer
		? getSupportUserProfilePicSrc({
				profilePic: repComposer.profilePic,
				agentProfilePic: agentProfilePicMap[repComposer.id],
				userId: repComposer.id,
				accountId: userSession.account?.id,
			})
		: isAgentAuthored
			? authorUserProfilePicSrc
			: authorUserProfilePicSrc || getSupportContactProfilePicSrc(ticket);

	return (
		<div>
			<div className={css(hp.sectionLabel)}>{label}</div>
			<div className={css(hp.senderRow)}>
				<Avatar
					imgSrc={avatarImgSrc}
					altText={authorName}
					fallbackText={getInitials(authorName, repComposer?.email ?? thread.authorEmail)}
					smallText
					styles={[hp.senderAvatar]}
				/>
				<span className={css(hp.senderName)}>
					{authorName}
					{isYou && <span className={css(hp.senderYou)}> &middot; you</span>}
				</span>
				<span className={css(hp.senderTime)}>{formatShortDateTime(thread.creationDate)}</span>
			</div>
			<div className={css(hp.messageText)}>{getThreadPreviewText(thread)}</div>
		</div>
	);
};

interface TicketHoverPreviewProps {
	ticket: ISupportTicket;
	agentNames: Record<string, string>;
	agentProfilePicMap: Record<string, string>;
	agentProfilePicEmailMap: Record<string, string>;
}

// Mount only while open to avoid fetching every row's thread history.
const TicketHoverPreview: React.FC<TicketHoverPreviewProps> = ({
	ticket,
	agentNames,
	agentProfilePicMap,
	agentProfilePicEmailMap,
}) => {
	const { data: threads, isLoading } = useSupportThreadsQuery(ticket.id, { refetchInterval: false });

	const messages = (threads?.values ?? [])
		.filter(thread => PREVIEW_THREAD_TYPES.includes(thread.type))
		.sort((a, b) => new Date(a.creationDate).getTime() - new Date(b.creationDate).getTime());
	const firstMessage = messages.length > 0 ? messages[0] : undefined;
	const latestMessage = messages.length > 1 ? messages[messages.length - 1] : undefined;
	const betweenCount = messages.length - 2;
	const latestLabel =
		latestMessage?.type === ThreadType.Note
			? 'Latest note'
			: latestMessage?.type === ThreadType.Forward
				? 'Latest forward'
				: 'Latest reply';

	return (
		<div className={css(hp.body)}>
			{isLoading && (
				<div className={css(hp.state)}>
					<LoadingSpinner type='small' />
				</div>
			)}
			{!isLoading && !firstMessage && (
				<div className={css(hp.state)}>{ticket.previewText || 'No messages on this ticket yet.'}</div>
			)}
			{firstMessage && (
				<PreviewMessage
					label='First message'
					thread={firstMessage}
					ticket={ticket}
					agentNames={agentNames}
					agentProfilePicMap={agentProfilePicMap}
					agentProfilePicEmailMap={agentProfilePicEmailMap}
				/>
			)}
			{latestMessage && (
				<>
					<div className={css(hp.divider)}>
						<span className={css(hp.dividerLine)} />
						{betweenCount > 0 && (
							<span className={css(hp.dividerLabel)}>
								+{betweenCount} {betweenCount === 1 ? 'message' : 'messages'}
							</span>
						)}
						<span className={css(hp.dividerLine)} />
					</div>
					<PreviewMessage
						label={latestLabel}
						thread={latestMessage}
						ticket={ticket}
						agentNames={agentNames}
						agentProfilePicMap={agentProfilePicMap}
						agentProfilePicEmailMap={agentProfilePicEmailMap}
					/>
				</>
			)}
		</div>
	);
};

export interface TicketHoverPopoverProps {
	ticket: ISupportTicket;
	agentNames: Record<string, string>;
	agentProfilePicMap?: Record<string, string>;
	agentProfilePicEmailMap?: Record<string, string>;
	children: React.ReactNode;
}

export const TicketHoverPopover: React.FC<TicketHoverPopoverProps> = ({
	ticket,
	agentNames,
	agentProfilePicMap = {},
	agentProfilePicEmailMap = {},
	children,
}) => {
	const [open, setOpen] = React.useState(false);
	const openTimer = React.useRef<number | undefined>(undefined);
	const closeTimer = React.useRef<number | undefined>(undefined);

	React.useEffect(
		() => () => {
			window.clearTimeout(openTimer.current);
			window.clearTimeout(closeTimer.current);
		},
		[]
	);

	const handleEnter = () => {
		window.clearTimeout(openTimer.current);
		window.clearTimeout(closeTimer.current);
		openTimer.current = window.setTimeout(() => setOpen(true), OPEN_DELAY_MS);
	};
	const handleLeave = () => {
		window.clearTimeout(openTimer.current);
		window.clearTimeout(closeTimer.current);
		closeTimer.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverAnchor asChild>
				<span className={css(hp.anchor)} onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
					{children}
				</span>
			</PopoverAnchor>
			<PopoverContent
				side='right'
				align='start'
				sideOffset={10}
				collisionPadding={12}
				styles={[hp.popover]}
				onOpenAutoFocus={event => event.preventDefault()}
				onMouseEnter={handleEnter}
				onMouseLeave={handleLeave}
			>
				<PopoverArrow width={14} height={7} />
				<div className={css(hp.header)}>
					<span className={css(hp.headerNumber)}>#{ticket.number}</span>
					<span className={css(hp.headerSubject)}>{ticket.subject || 'No subject'}</span>
				</div>
				<TicketHoverPreview
					ticket={ticket}
					agentNames={agentNames}
					agentProfilePicMap={agentProfilePicMap}
					agentProfilePicEmailMap={agentProfilePicEmailMap}
				/>
			</PopoverContent>
		</Popover>
	);
};
